// =============================================================================
// PAGE GESTION DES DOCUMENTS SOURCES (MODÈLES OFFICIELS)
// =============================================================================
// Interface pour gérer les documents sources officiels associés aux templates :
// - Upload des modèles officiels (CC CEE, AH CEE, etc.)
// - Association avec les templates
// - Téléchargement des références
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  FileUp,
  FileText,
  Download,
  Trash2,
  Link2,
  Eye,
  RefreshCw,
  Loader2,
  Info,
  Upload,
  File,
  Calendar,
  Hash,
  Building2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DocumentsSourcesManager({ tenantId = "climatpac" }) {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  // États UI
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // Formulaire upload
  const [formData, setFormData] = useState({
    nom: "",
    version: "1.0",
    origine: "",
    notes: "",
    template_code: "",
    fichier: null
  });

  // Fonction pour obtenir les headers avec authentification (toujours frais)
  const getAuthHeaders = () => {
    const token = localStorage.getItem("session_token");
    return {
      ...enteteTenant(tenantId),
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  // ==========================================================================
  // CHARGEMENT DES DONNÉES
  // ==========================================================================

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [docsRes, templatesRes] = await Promise.all([
        fetch(`${API}/api/metier/documents-sources`, { headers }),
        fetch(`${API}/api/metier/documents-engine/templates`, { headers })
      ]);

      if (docsRes.ok) {
        const data = await docsRes.json();
        setDocuments(data);
      }

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error("Erreur chargement:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ==========================================================================
  // ACTIONS
  // ==========================================================================

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!formData.nom || !formData.fichier) {
      toast.error("Nom et fichier sont requis");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("nom", formData.nom);
      formDataUpload.append("version", formData.version);
      formDataUpload.append("origine", formData.origine);
      formDataUpload.append("notes", formData.notes);
      formDataUpload.append("template_code", formData.template_code);
      formDataUpload.append("fichier", formData.fichier);

      const res = await fetch(`${API}/api/metier/documents-sources`, {
        method: "POST",
        headers: { ...enteteTenant(tenantId) },
        body: formDataUpload
      });

      if (res.ok) {
        toast.success("Document source uploadé avec succès");
        setShowUploadDialog(false);
        resetForm();
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de l'upload");
      }
    } catch (error) {
      console.error("Erreur upload:", error);
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDocument) return;

    try {
      const res = await fetch(`${API}/api/metier/documents-sources/${selectedDocument.id}`, {
        method: "DELETE",
        headers
      });

      if (res.ok) {
        toast.success("Document source supprimé");
        setShowDeleteDialog(false);
        setSelectedDocument(null);
        loadData();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur suppression:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDownload = async (doc) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/api/metier/documents-sources/${doc.id}/download`, { headers });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.fichier_nom || "document.pdf";
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Téléchargement en cours");
      } else {
        toast.error("Erreur lors du téléchargement");
      }
    } catch (error) {
      console.error("Erreur téléchargement:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  const resetForm = () => {
    setFormData({
      nom: "",
      version: "1.0",
      origine: "",
      notes: "",
      template_code: "",
      fichier: null
    });
  };

  const formatBytes = (bytes) => {
    if (!bytes) return "-";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} Ko`;
    return `${(kb / 1024).toFixed(1)} Mo`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // ==========================================================================
  // RENDU
  // ==========================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[var(--oh-text-muted)] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="documents-sources-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-[var(--oh-text)] flex items-center gap-3">
            <FileUp className="w-7 h-7 text-[var(--oh-info-text)]" />
            Documents Sources (Modèles Officiels)
          </h1>
          <p className="text-[var(--oh-text-muted)] mt-1">
            Gérez les documents officiels de référence pour vos templates CEE/MPR
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadData}
            className="border-[var(--oh-border)]"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button 
            onClick={() => setShowUploadDialog(true)}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            Uploader un modèle
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-[var(--oh-accent-bg)] border-[var(--oh-accent)]">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-[var(--oh-accent-text)] flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-[var(--oh-accent-text)] font-medium">Documents de référence</p>
              <p className="text-[var(--oh-accent-text)] mt-1">
                Uploadez les documents officiels (Cadre de Contribution CEE, Attestation sur l'Honneur, etc.) 
                qui servent de référence pour les templates. Ces documents seront associés aux templates 
                correspondants pour garantir la conformité.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des documents */}
      <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
        <CardHeader>
          <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[var(--oh-accent-text)]" />
            Documents uploadés
          </CardTitle>
          <CardDescription className="text-[var(--oh-text-muted)]">
            {documents.length} document(s) de référence
          </CardDescription>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-12">
              <FileUp className="w-12 h-12 text-[var(--oh-text-secondary)] mx-auto mb-4" />
              <p className="text-[var(--oh-text-muted)] text-lg">Aucun document source</p>
              <p className="text-[var(--oh-text-secondary)] text-sm mb-4">
                Commencez par uploader un modèle officiel
              </p>
              <Button 
                onClick={() => setShowUploadDialog(true)}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                Uploader un modèle
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {documents.map((doc) => {
                const linkedTemplate = templates.find(t => t.code === doc.template_code);
                
                return (
                  <div 
                    key={doc.id}
                    className="p-4 bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg hover:border-[var(--oh-border)] transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-[var(--oh-info-bg)] rounded-lg">
                          <File className="w-6 h-6 text-[var(--oh-info-text)]" />
                        </div>
                        <div>
                          <h3 className="text-[var(--oh-text)] font-medium">{doc.nom}</h3>
                          <p className="text-[var(--oh-text-secondary)] text-sm">{doc.fichier_nom}</p>
                          
                          <div className="flex items-center gap-4 mt-2 text-xs text-[var(--oh-text-muted)]">
                            <div className="flex items-center gap-1">
                              <Badge variant="outline" className="text-[var(--oh-text-muted)]">
                                v{doc.version}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(doc.uploaded_at)}
                            </div>
                            <div className="flex items-center gap-1">
                              {formatBytes(doc.fichier_taille)}
                            </div>
                            {doc.fichier_hash && (
                              <div className="flex items-center gap-1 font-mono">
                                <Hash className="w-3 h-3" />
                                {doc.fichier_hash.slice(0, 12)}...
                              </div>
                            )}
                          </div>

                          {doc.origine && (
                            <div className="flex items-center gap-1 mt-2 text-xs text-[var(--oh-text-secondary)]">
                              <Building2 className="w-3 h-3" />
                              Origine: {doc.origine}
                            </div>
                          )}
                          
                          {linkedTemplate && (
                            <div className="flex items-center gap-2 mt-2">
                              <Link2 className="w-3 h-3 text-[var(--oh-success-text)]" />
                              <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] text-xs">
                                Lié à: {linkedTemplate.nom}
                              </Badge>
                            </div>
                          )}
                          
                          {doc.notes && (
                            <p className="text-[var(--oh-text-secondary)] text-xs mt-2 italic">
                              {doc.notes}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(doc)}
                          title="Télécharger"
                        >
                          <Download className="w-4 h-4 text-[var(--oh-text-muted)]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedDocument(doc);
                            setShowDeleteDialog(true);
                          }}
                          title="Supprimer"
                          className="text-[var(--oh-error-text)] hover:bg-[var(--oh-error-bg)]"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Upload */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <Upload className="w-5 h-5 text-[var(--oh-info-text)]" />
              Uploader un document source
            </DialogTitle>
            <DialogDescription className="text-[var(--oh-text-muted)]">
              Ajoutez un modèle officiel de référence (PDF)
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nom" className="text-[var(--oh-text)]">Nom du document *</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => setFormData({...formData, nom: e.target.value})}
                placeholder="Ex: Cadre de Contribution CEE - Premium Energy 2025"
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="version" className="text-[var(--oh-text)]">Version</Label>
                <Input
                  id="version"
                  value={formData.version}
                  onChange={(e) => setFormData({...formData, version: e.target.value})}
                  placeholder="1.0"
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="origine" className="text-[var(--oh-text)]">Origine</Label>
                <Input
                  id="origine"
                  value={formData.origine}
                  onChange={(e) => setFormData({...formData, origine: e.target.value})}
                  placeholder="Ex: Premium Energy"
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="template_code" className="text-[var(--oh-text)]">Associer à un template</Label>
              <Select
                value={formData.template_code}
                onValueChange={(value) => setFormData({...formData, template_code: value})}
              >
                <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]">
                  <SelectValue placeholder="Sélectionner un template (optionnel)" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                  <SelectItem value="none">Aucun</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.code} value={t.code}>
                      {t.nom} ({t.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes" className="text-[var(--oh-text)]">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Notes ou commentaires..."
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)] min-h-[80px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fichier" className="text-[var(--oh-text)]">Fichier PDF *</Label>
              <Input
                id="fichier"
                type="file"
                accept=".pdf"
                onChange={(e) => setFormData({...formData, fichier: e.target.files?.[0] || null})}
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)] file:bg-orange-600 file:text-[var(--oh-accent-contrast)] file:border-0 file:rounded file:px-3 file:py-1 file:mr-3 cursor-pointer"
                required
              />
              {formData.fichier && (
                <p className="text-xs text-[var(--oh-text-muted)]">
                  Fichier sélectionné: {formData.fichier.name} ({formatBytes(formData.fichier.size)})
                </p>
              )}
            </div>

            <DialogFooter className="pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowUploadDialog(false);
                  resetForm();
                }}
              >
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={uploading}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Upload en cours...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Uploader
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Suppression */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--oh-text)]">Supprimer ce document ?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--oh-text-muted)]">
              Cette action supprimera définitivement le document "{selectedDocument?.nom}".
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[var(--oh-border)]">Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
